import { Request } from '../request';
import {
    IproviderCreate,
    ProviderBasicInfo,
    IUpdatePasswordFormData,
    IProvderAgreementSignInput,
    EProviderType,
    TCreateRefund,
    TCreateUnifiedRefund,
    ProviderFeedbackCreateReq,
} from 'types/provider';
import { ENotificationType, EServiceType, IntakeqForm, License, Physician, Service } from 'types/common';
import { IAddProfile } from 'types/reputation';
import { TSubscriptionType } from 'types/subscription';
import { GetListCommon } from 'types/operation';
import { ERegisterType } from 'api/public';
import { isKlarityHost, shouldUseKlarityUi } from 'utils/common';
import {
    TSaveOrUpdateInvoice,
    TGetPatientPaymentMethodListParams,
    TPaymentCheckoutVo,
    TProviderAddPaymentMethodReq,
    TInvoicePayRequest,
    TInvoicePayResult,
} from 'types/invoice';
import type { ProviderOverviewResponse } from 'types/providerOverview';

export const providerCreate = (data: IproviderCreate) =>
    Request.post({ url: '/schedule-server/provider/create', params: data, disableErrorNote: true });

export const providerSendVerification = (email: string) =>
    Request.post({ url: '/schedule-server/provider/getByEmail', params: { email } });
export const providerSendResume = (email: string) =>
    Request.post({ url: '/schedule-server/provider/resume/', params: { email }, disableErrorNote: true });

export const providerFetchByEmail = (email: string) =>
    Request.post({ url: '/schedule-server/provider/getByEmail', params: { email } });

export const providerFetch = (id: number) => Request.get({ url: `/schedule-server/provider/get` });

export const providerBasicInfoUpdate = (data: Partial<ProviderBasicInfo>, id: number) =>
    Request.put<Partial<ProviderBasicInfo>>({
        url: `/schedule-server/provider/updateBasicInfo/${id}`,
        params: data,
        disableErrorNote: true,
    });

//use state, type & secondType as key
export const providerLicenseUpdate = (data: Partial<License>, id: number, scheduleToken?: string) =>
    Request.put<Partial<License>>({
        url: `/schedule-server/license/updateByProviderId/${id}`,
        params: data,
        disableErrorNote: true,
        scheduleToken,
    });

export const providerLicenseBatchUpdate = (data: Partial<License>[]) =>
    Request.post({ url: '/schedule-server/license/practice/update', params: data });

// for DEA / CSC / CMEC
export const providerLicenseBatchByType = (data: Partial<License>[], type: string, scheduleToken?: string) =>
    Request.post({ url: `/schedule-server/license/batch/update/${type}`, params: data, scheduleToken });

export const providerPhysicianUpdate = (data: Partial<Physician>, id: number) =>
    Request.put<Partial<Physician>>({
        url: `/schedule-server/physician/saveByProviderId/${id}`,
        params: data,
        disableErrorNote: true,
    });

export const providerLicenseFetch = (id: number) =>
    Request.get({ url: `/schedule-server/license/listByProviderId/${id}` });

export const providerPhysicianFetch = (id: number) =>
    Request.get({ url: `/schedule-server/physician/getByProviderId/${id}` });

export const getIntakeqForm = () =>
    Request.get<IntakeqForm[]>({
        url: '/schedule-server/intakeq/form/template/list',
        disableErrorNote: true,
    });

export const providerChannelServiceFetch = () => Request.get({ url: '/schedule-server/provider/channel/service/list' });

export const surveyChannelUsing = (channelIds: number[]) =>
    Request.post({ url: '/schedule-server/provider/channel/listed/save', params: channelIds });

export const surveyCareMost = (careList: string[]) =>
    Request.post({ url: '/schedule-server/provider/channel/care/save', params: careList });

export const surveyComplete = () => Request.put({ url: '/schedule-server/provider/channel/guide/complete' });

export const getChannelHome = () => Request.get({ url: '/schedule-server/provider/channel/home' });

export const providerListedChannelFetch = (providerId: number) =>
    Request.get({ url: `/schedule-server/openapi/channel/list/${providerId}` });

export const providerBaseServiceList = (providerId: number) =>
    Request.get<Service[]>({ url: `/schedule-server/providerService/listByProviderId/?providerId=${providerId}` });

export const providerChannelServiceList = (providerId: number, channelId: number) =>
    Request.get<Service[]>({ url: `/schedule-server/openapi/channel/service/list/${providerId}/${channelId}` });

export const providerServiceTemplateList = () =>
    Request.get<Service[]>({ url: '/schedule-server/serviceTemplate/list' });

export const getHomeInfo = () => Request.get({ url: '/schedule-server/provider/home', disableErrorNote: true });

export const getHomeConfiglList = () => Request.get({ url: '/schedule-server/homeConfig/list' });

export const updateProviderPassword = (data: IUpdatePasswordFormData) =>
    Request.post({ url: '/schedule-server/provider/saveEmailPassword', params: data });

export const getProviderEmailNotify = () => Request.get({ url: '/schedule-server/provider/getEmailNotify' });

//"jim.a@gmail.com,perter.b@gmal.com"
export const updateProviderEmailNotif = (emailNotify: string) =>
    Request.post({ url: '/schedule-server/provider/updateEmailNotify', params: { emailNotify } });

export const providerSeoCityUpdate = (data: string[], state: string) =>
    Request.put({ url: '/schedule-server/provider/seo/city/update', params: { seoCity: JSON.stringify(data), state } });

export const providerSeoCountryUpdate = (data: string[], state: string) =>
    Request.put({
        url: '/schedule-server/provider/seo/country/update',
        params: { seoCountry: JSON.stringify(data), state },
    });

export const providerSeoLandmarkUpdate = (data: string[], state: string) =>
    Request.put({
        url: '/schedule-server/provider/seo/landmark/update',
        params: { seoLandmark: JSON.stringify(data), state },
    });

export const providerSeoZipUpdate = (data: string[], state: string) =>
    Request.put({ url: '/schedule-server/provider/seo/zip/update', params: { seoZip: JSON.stringify(data), state } });

export const providerRemoveSeo = () => Request.delete({ url: '/schedule-server/provider/app/seo/remove' });

export const providerAddSeo = () => Request.post({ url: '/schedule-server/provider/app/seo/add' });

export const getLocalSeoInfoByZipCode = (zipCode: string) =>
    Request.get({ url: `/schedule-server/provider/seo/google/${zipCode}` });

export const getSuggestCityByInput = (input: string) =>
    Request.get({ url: `/schedule-server/provider/seo/google/city/${input}` });

export const replyFeedback = (feedbackId: number, item: { subject: string; message: string }) =>
    Request.post({ url: `/schedule-server/provider/reply/${feedbackId}`, params: item });

export const reputationSendSurvey = (patientId: number[]) =>
    Request.post({ url: '/schedule-server/reputation/manage/sendSurvey', params: { patientId } });

export const reputationAddProfile = (id: number, data: IAddProfile) =>
    Request.put({ url: `/schedule-server/provider/update/${id}`, params: data });

export const getProviderAgreementInfo = (plan: string) =>
    Request.get({ url: `/schedule-server/payPlan/agreement?type=${plan}` });

export const signProviderAgreement = (data: IProvderAgreementSignInput) =>
    Request.post({ url: '/schedule-server/provider/contract/signed', params: data });

export const signKlarityHealthAgreement = () =>
    Request.post({ url: '/schedule-server/provider/agreement/klarity-health/sign' });

export const saveNoPrescribeLicense = () => Request.put({ url: '/schedule-server/license/dea/not/prescribe' });

export const providerGetKlarityResources = (data: GetListCommon) =>
    Request.post({ url: '/schedule-server/provider/resources/pageList', params: data });

export const providerGetKlarityResourcesUrl = (resourceKey: string) =>
    Request.post({ url: '/schedule-server/common/private/file/signed', params: { resourceKey } });

export const updateProviderNotificationConfig = (
    notificationEmail: string | null,
    notificationPhoneNumber: string | null,
    tel: string | null,
) =>
    Request.post({
        url: '/schedule-server/provider/updateProviderNotificationConfig',
        params: {
            notificationEmail,
            notificationPhoneNumber,
            tel,
        },
    });

export const getGoogleAuthStatus = () =>
    Request.get({ url: '/schedule-server/google/calendar/checkAuthorizationStatus' });

export const getGoogleAuthUrl = () =>
    Request.get({
        url: '/schedule-server/google/calendar/auth',
        params: { providerType: shouldUseKlarityUi() ? ERegisterType.KLARITY_PORTAL : ERegisterType.KIWI_PORTAL },
    });

export const disconnetFromGoogle = () => Request.put({ url: '/schedule-server/google/calendar/disconnect' });

export type TGetPaymentBillingList = {
    size: number;
    current: number;
    billingStatus?: string[];
    patientName?: string;
    chargeType?: string[];
    orderKey?: 'CREATE_AT' | 'AMOUNT' | 'PAYOUT' | 'ISSUE_DATE' | 'DUE_DATE';
    orderType?: 'ASC' | 'DESC';
    clientId?: number;
    timeRangeMonths?: number;
};

//todo
export const getPaymentBillingList = (params: TGetPaymentBillingList) =>
    Request.get({
        url: '/schedule-server/provider/billing/list',
        params,
    });

export const getPaymentEventList = (id: number) =>
    Request.get({
        url: `/schedule-server/provider/billing/${id}/event/list`,
    });

export const getTextVisitPaymentEventList = (id: number) =>
    Request.get({
        url: `/schedule-server/provider/billing/${id}/klarity-user-billing-events`,
    });

export const getEarning = (params?: { timeRangeMonths?: number }) =>
    Request.get({
        url: '/schedule-server/provider/billing/earnings',
        params,
    });

export type ProviderPayoutListReq = {
    current?: number;
    size?: number;
    orderKey?:
        | 'CREATE_AT'
        | 'LAST_ACTION_AT'
        | 'CONTRACT_VERSION'
        | 'LAST_UPDATE_AT'
        | 'UPDATE_AT'
        | 'AMOUNT'
        | 'CHARGE_SUCCEED_AT'
        | 'SORT'
        | 'START_AT';
    orderType?: 'ASC' | 'DESC';
    providerId?: number;
    sortBy?: 'PAYOUT_DATE' | 'AMOUNT';
    timeRangeMonths?: number;
};

export type ProviderPayoutVo = {
    amount: number;
    paymentStatus: string;
    payoutDate: string; // MM/dd/yyyy
    payoutId: number;
    payoutSource: string;
    // Present in the group practice "Practice View" payout list so payouts can be attributed to a
    // provider.
    providerName?: string;
    providerAvatar?: string;
    providerCredential?: string;
    providerTitle?: string;
    providerId?: number;
};

type OrderItem = {
    asc?: boolean;
    column?: string;
};

export type Page<T> = {
    records: T[];
    total: number;
    size: number;
    current: number;
    pages?: number;
    orders?: OrderItem[];
    searchCount?: boolean;
    optimizeCountSql?: boolean;
    maxLimit?: number;
    countId?: string;
};

export type R<T> = {
    code: number;
    data: T;
    message?: string;
};

export const getPayoutHistoryList = (params: ProviderPayoutListReq) =>
    Request.post({
        url: '/schedule-server/provider/billing/payout/list',
        params,
    });

export const getPayoutTotalAmount = (params: ProviderPayoutListReq) =>
    Request.post<number>({
        url: '/schedule-server/provider/billing/payout/total-amount',
        params,
    });

// ---- Group practice financials (Practice View). Same shapes as the provider endpoints above. ----
export const getPracticeEarning = (params?: { timeRangeMonths?: number }) =>
    Request.get({
        url: '/schedule-server/provider/billing/practice/earnings',
        params,
    });

export const getPracticePayoutHistoryList = (params: ProviderPayoutListReq) =>
    Request.post({
        url: '/schedule-server/provider/billing/practice/payout/list',
        params,
    });

export const getPracticePayoutTotalAmount = (params: ProviderPayoutListReq) =>
    Request.post<number>({
        url: '/schedule-server/provider/billing/practice/payout/total-amount',
        params,
    });

export const createRefund = (param: TCreateRefund) =>
    Request.post({
        url: '/schedule-server/provider/billing/refund',
        params: param,
    });

export const createUnifiedRefund = (param: TCreateUnifiedRefund) =>
    Request.post({
        url: '/schedule-server/provider/unified-billing/refund',
        params: param,
    });

export const createPracticeUnifiedRefund = (param: TCreateUnifiedRefund) =>
    Request.post({
        url: '/schedule-server/provider/unified-billing/practice/refund',
        params: param,
    });

// Nested API export (non-breaking): providerApi.billing.payout.list(...)
export const providerApi = {
    billing: {
        payout: {
            list: getPayoutHistoryList,
            totalAmount: getPayoutTotalAmount,
        },
        practice: {
            payout: {
                list: getPracticePayoutHistoryList,
                totalAmount: getPracticePayoutTotalAmount,
            },
        },
    },
};

export const getProviderOverview = () =>
    Request.get<ProviderOverviewResponse>({ url: '/schedule-server/provider/overview' });

export const getAvailableHoursInNextNHours = (hours: number) =>
    Request.get({ url: '/schedule-server/provider/home/getAvailableHoursInNextNHours', params: { hours } });

export const initNotificationDetail = () => Request.post({ url: '/schedule-server/provider/notification/initialize' });

export const getNotificationDetail = () => Request.get({ url: '/schedule-server/provider/notification/detail' });

export const setNotificationRecipients = (emails: string[], providerId: number) =>
    Request.post({ url: '/schedule-server/provider/notification/recipients/batch', params: { emails, providerId } });

export const toggleNotificationType = (notificationType: ENotificationType, providerId: number, enabled: boolean) =>
    Request.post({
        url: '/schedule-server/provider/notification/type/toggle',
        params: { notificationType, providerId, enabled },
    });

export const addNotificationType = (notificationTypeSettingId: number, recipientId: number) =>
    Request.post({
        url: '/schedule-server/provider/notification/type-recipient/add',
        params: { notificationTypeSettingId, recipientId },
    });

export const removeNotificationType = (notificationTypeSettingId: number, recipientId: number) =>
    Request.post({
        url: '/schedule-server/provider/notification/type-recipient/remove',
        params: { notificationTypeSettingId, recipientId },
    });

export const disableKlarityService = (serviceItemIds: number[]) =>
    Request.post({ url: '/schedule-server/provider/klarity/service/disable', params: serviceItemIds });

// {
//     "bookingReason": "",
//     "clientId": 0,
//     "relatedTextVisitId": 0,
//     "serviceItemId": 0,
//     "textVisitBookingType": ""
//   }

type TCreateTextVisit = {
    bookingReason: string;
    clientId: number;
    relatedTextVisitId?: number;
    serviceItemId: number;
    serviceType: string;
    medications?: string[];
    recurrenceRule?: string;
    repeatDesc?: string;
    dtstart?: string;
};

export const createTextVisit = (params: TCreateTextVisit, overrideProviderId?: number) =>
    Request.post({
        url: '/schedule-server/text/visit/provider/create',
        params,
        overrideProviderId,
    });

export const getIntakeFormConfig = (specialty: string, serviceType: EServiceType) =>
    Request.get({ url: '/schedule-server/intake-forms/required', params: { specialty, serviceType } });

export const textVisitDecline = (textVisitId: number, declineReason: string) =>
    Request.post({ url: '/schedule-server/text/visit/decline', params: { textVisitId, declineReason } });

export const getInvoiceDraftList = (params: TGetPaymentBillingList) =>
    Request.get({
        url: '/schedule-server/provider/invoice/draftList',
        params,
    });

export const saveOrUpdateInvoice = (params: TSaveOrUpdateInvoice, overrideProviderId?: number) =>
    Request.post({
        url: '/schedule-server/provider/invoice/saveOrUpdate',
        params,
        overrideProviderId,
    });

export const cancelInvoice = (invoiceNo: string) =>
    Request.post({
        url: `/schedule-server/provider/invoice/cancel/${invoiceNo}`,
        params: {
            status: 'CANCELLED',
        },
    });

export const getInvoiceDetail = (invoiceNo: string) =>
    Request.get({
        url: `/schedule-server/provider/invoice/detail?invoiceNo=${invoiceNo}`,
    });

// Practice View: invoices of any provider within the caller's practice scope
export const getPracticeInvoiceDetail = (invoiceNo: string) =>
    Request.get({
        url: `/schedule-server/provider/invoice/practice/detail?invoiceNo=${invoiceNo}`,
    });

export const getInvoiceOptions = (state: string, clientId?: number) => {
    const data: any = {
        state,
    };
    if (clientId) {
        data.clientId = clientId;
    }
    return Request.get({
        url: '/schedule-server/provider/invoice/dropdown-options',
        params: data,
    });
};

export const getInvoiceList = (params: TGetPaymentBillingList) =>
    Request.get({
        url: '/schedule-server/provider/invoice/list',
        params,
    });

// Group practice "Practice View" invoice list — practice-wide (all providers), same shape as
// getInvoiceList. Records include providerName for attribution.
export const getPracticeInvoiceList = (params: TGetPaymentBillingList) =>
    Request.get({
        url: '/schedule-server/provider/invoice/practice/list',
        params,
    });

export const getInvoiceCounts = (clientId: number) =>
    Request.get({
        url: `/schedule-server/provider/invoice/counts?clientId=${clientId}`,
    });

type TPayInvoice = {
    invoiceNo: string;
    paymentMethodId: string;
    paymentToken: string;
    paymentType: string;
};

export const payInvoice = (params: TPayInvoice) =>
    Request.post({
        url: `/schedule-server/provider/invoice/pay`,
        params,
    });
export const getPatientPaymentMethodList = (params: TGetPatientPaymentMethodListParams) =>
    Request.get({
        url: '/schedule-server/provider/invoice/payment-method/list',
        params,
    });

export const getPaymentCheckoutToken = () =>
    Request.get<TPaymentCheckoutVo>({
        url: '/schedule-server/provider/invoice/payment/token',
    });

export const addPatientPaymentMethod = (params: TProviderAddPaymentMethodReq) =>
    Request.post({
        url: '/schedule-server/provider/invoice/payment-method/add',
        params,
    });

export const providerInvoicePay = (params: TInvoicePayRequest) =>
    Request.post({
        url: '/schedule-server/provider/invoice/pay',
        params,
    });

export const providerReminderSend = (
    clientId: number,
    action: 'PROVIDER_PATIENT_INVITATION_WHEN_NOT_SENT' | 'PROVIDER_PATIENT_ONBOARDING_IN_PROGRESS_REMINDER',
) => Request.post({ url: '/schedule-server/provider/resume/', params: { clientId, action } });

export const getCurrentUser = () => Request.get({ url: '/schedule-server/api/auth/currentUser' });

// 通用 provider 反馈入口，任何页面可用（type 区分采集来源，约束见 ProviderFeedbackCreateReq）；
// 无唯一键，重复提交写多行，不触发任何通知
export const submitProviderFeedback = (req: ProviderFeedbackCreateReq) =>
    Request.post({ url: '/schedule-server/provider/feedback', params: req });
